import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { OEContext } from '@aytm/app/stats/page/comments'

import { REJECTED_OPTIONS } from '@aytm/app/stats/page/comments/lib/constants'

import useResponseStatus from '@aytm/app/stats/page/comments/hooks/useResponseStatus'

import { Dropdown } from 'primereact/dropdown'

import Button from '@aytm/lib/rfd-ui/components/basic/button'
import TextField from '@aytm/lib/rfd-ui/components/basic/text-field'
import NotificationPopup from '@aytm/app/stats/page/comments/comments/components/Response/NotificationPopup'

import style from '@aytm/app/stats/page/comments/comments/styles/Response.module.scss'

const Reject = memo(
  ({
    responseId,
    rejectReason,
    setRejectReason,
    onChangeResponseStatus,
    onCloseNotification,
    popupNotificationsDisabled
  }) => {
    const { searchState } = useContext(OEContext)
    const { request, isAdmin } = searchState
    const { pageSize } = request

    const [otherReason, setOtherReason] = useState('')

    const rejectSelect = useRef()
    const otherInput = useRef()

    const isOnePerPage = useMemo(() => 1 == pageSize, [pageSize])

    const itemTemplate = option => <span dangerouslySetInnerHTML={{ __html: option.text }}></span>

    const onReject = useCallback(event => setRejectReason(event.value), [setRejectReason])

    const onConfirmReject = useCallback(() => {
      onChangeResponseStatus(responseId, 'reject', rejectReason)
      setRejectReason(rejectReason)
    }, [onChangeResponseStatus, responseId, rejectReason, setRejectReason])

    const onCancelReject = useCallback(() => {
      setRejectReason('')
    }, [setRejectReason])

    const onChangeOtherReason = useCallback(reason => {
      setOtherReason(reason)
    }, [])

    const onOtherReject = useCallback(() => {
      if (otherReason) {
        onChangeResponseStatus(responseId, 'reject', otherReason)
        setRejectReason('')
      }
    }, [otherReason, onChangeResponseStatus, responseId, setRejectReason])

    const onRejectReasonKeyDown = useCallback(
      event => {
        if (27 == event.keyCode) {
          setOtherReason('')
          setRejectReason('')
          onCancelReject()
        }
        if (13 == event.keyCode) {
          if (rejectReason) {
            onConfirmReject()
          }
          if (otherReason) {
            onOtherReject()
          }
        }
      },
      [setRejectReason, onCancelReject, rejectReason, otherReason, onConfirmReject, onOtherReject]
    )

    const { status, isQAMode, qaAction, qaProcessedResponseId } = useResponseStatus(
      OEContext,
      responseId,
      'reject',
      event => {
        if (
          isOnePerPage &&
          (51 == event.keyCode || 99 == event.keyCode || 34 == event.keyCode) &&
          event.shiftKey &&
          rejectSelect.current
        ) {
          rejectSelect.current.show()
        }
      }
    )

    useEffect(() => {
      if (rejectReason) {
        window.addEventListener('keydown', onRejectReasonKeyDown)
      }
      return () => {
        window.removeEventListener('keydown', onRejectReasonKeyDown)
      }
    }, [rejectReason, onRejectReasonKeyDown])

    switch (true) {
      case 'true' == status && !(isQAMode && isOnePerPage):
        return <>Rejected</>
      case 'loading' == status ||
        (isQAMode && 'reject' == qaAction && ('true' == status || qaProcessedResponseId == responseId)):
        return <i className="icon-aytmfd-refresh-v2 aytmfd-spin" />
      case 'object' == typeof status && 'error' == status.type:
        return <>Error</>
      default: {
        switch (true) {
          case 'other' == rejectReason: {
            return (
              <div className="reject-reason">
                <span onClick={onCancelReject}>
                  <i className="icon-aytmfd-close-v2" />
                </span>
                <TextField
                  ref={otherInput}
                  placeholder="Please type a reason for rejection..."
                  width="250px"
                  debounce={100}
                  value={otherReason}
                  autoFocus
                  onKeyDown={onRejectReasonKeyDown}
                  onChange={onChangeOtherReason}
                />
                <Button label="Reject" disabled={!otherReason} onClick={onOtherReject} />
              </div>
            )
          }
          case Boolean(rejectReason): {
            return (
              <div className={classNames(style['response-header-confirmation'], style['reject-confirmation'])}>
                <span className={style['response-header-confirmation-button']} onClick={onCancelReject}>
                  Cancel
                </span>
                <span className={style['response-header-confirmation-button']} onClick={onConfirmReject}>
                  <span className={style['reject-confirm']}>Reject</span>
                </span>
              </div>
            )
          }
          default:
            return (
              <div
                className={classNames(style['response-header-reject'], {
                  [style['has-notification']]: !popupNotificationsDisabled['reject'] && !isAdmin
                })}
              >
                <NotificationPopup
                  action="reject"
                  type="reject"
                  status="no"
                  disabled={popupNotificationsDisabled['reject'] || isAdmin}
                  onClosePopup={onCloseNotification}
                >
                  <Dropdown
                    ref={rejectSelect}
                    className="p-dropdown-inline p-dropdown-rounded p-dropdown-specials p-dropdown-left"
                    placeholder="Reject"
                    value={rejectReason}
                    options={REJECTED_OPTIONS}
                    optionValue="value"
                    optionLabel="text"
                    itemTemplate={itemTemplate}
                    valueTemplate={() => <>Reject</>}
                    fixedDropdownDir="bottom"
                    onChange={onReject}
                  />
                </NotificationPopup>
              </div>
            )
        }
      }
    }
  }
)

Reject.propTypes = {
  popupNotificationsDisabled: PropTypes.object,
  onCloseNotification: PropTypes.func,
  response: PropTypes.object,
  responseId: PropTypes.number,
  rejectReason: PropTypes.string,
  setRejectReason: PropTypes.func,
  isRejected: PropTypes.bool,
  onChangeResponseStatus: PropTypes.func
}

Reject.displayName = 'Reject'

export default Reject
